import { ForwardedRef, ReactNode, forwardRef } from 'react';
import { Platform, Pressable, PressableProps, View } from 'react-native';
import { tv } from '@/common/theme/tv.config';
import { bgVariants } from '@/common/theme/variants';
import { TColors, TSizeSpacing } from '@coverflex-tech/hypernova-design-tokens';

export interface ICardProps extends Omit<PressableProps, 'onClick'> {
  className?: string;
  plain?: boolean;
  backgroundColor?: keyof TColors['light']['semantic']['surface'];
  size?: Extract<TSizeSpacing, 'sm' | 'md'>;
  border?: boolean;
  children: ReactNode;
}

export const Card = forwardRef(
  ({ border = true, ...props }: ICardProps, ref: ForwardedRef<View>) => {
    const viewProps = getViewProps(props);

    return (
      <Pressable
        {...viewProps}
        className={styles.root({
          plain: props.plain,
          backgroundColor: props.backgroundColor,
          clickable: !!props.onPress && !props.disabled,
          border,
          className: props.className,
        })}
        ref={ref}
      >
        {props.children}
      </Pressable>
    );
  }
);
Card.displayName = 'Card';

function getViewProps(props: ICardProps) {
  const { size, plain, backgroundColor, ...viewProps } = props;
  return viewProps;
}

const styles = {
  root: tv({
    base: 'p-md active:shadow-none md:p-lg',
    variants: {
      plain: {
        true: 'shadow-none hover:shadow-none',
        false: [
          'shadow-resting-neutral10 hover:shadow-hovering-neutral10',
          Platform.OS === 'web' && 'transition-shadow duration-150 ease-in-out',
        ],
      },
      border: {
        true: 'rounded-xl',
        false: '',
      },
      clickable: {
        true: 'cursor-pointer',
        false: 'cursor-default',
      },
      backgroundColor: bgVariants,
    },
    defaultVariants: {
      border: true,
      backgroundColor: 'neutral10',
      size: 'sm',
    },
  }),
};
